import React, { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { countStates, QuestionState } from './examState';

const COLOURS: Record<QuestionState, { background: string; border: string; text: string }> = {
  answered: { background: '#0F5C55', border: '#0F5C55', text: '#FFFFFF' },
  skipped: { background: '#F7EBD4', border: '#C08A2E', text: '#7A5410' },
  unseen: { background: '#FFFFFF', border: '#C9D2CE', text: '#3E4B47' },
};

const LEGEND: { state: QuestionState; label: string }[] = [
  { state: 'answered', label: 'Answered' },
  { state: 'skipped', label: 'Skipped' },
  { state: 'unseen', label: 'Not seen' },
];

/**
 * Every question on the paper at a glance, and a way to jump to any of them.
 *
 * Colour alone would not tell a student anything they could rely on, so the
 * current question also carries a ring and every button names its own state to
 * a screen reader.
 */
export const QuestionNavigator: FC<{
  states: QuestionState[];
  current: number;
  onJump: (index: number) => void;
}> = ({ states, current, onJump }) => {
  const counts = countStates(states);

  return (
    <Box>
      <Stack direction='row' spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        {LEGEND.map((entry) => (
          <Stack key={entry.state} direction='row' spacing={0.75} alignItems='center'>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '4px',
                background: COLOURS[entry.state].background,
                border: `1px solid ${COLOURS[entry.state].border}`,
              }}
            />
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {entry.label} ({counts[entry.state]})
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(2.6rem, 1fr))',
          gap: 1,
        }}>
        {states.map((state, index) => {
          const colours = COLOURS[state];
          const isCurrent = index === current;

          return (
            <Box
              component='button'
              type='button'
              key={index}
              onClick={() => onJump(index)}
              aria-current={isCurrent ? 'true' : undefined}
              aria-label={`Question ${index + 1}, ${state === 'unseen' ? 'not seen' : state}`}
              sx={{
                height: '2.6rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '.95rem',
                fontFamily: 'inherit',
                background: colours.background,
                color: colours.text,
                border: `1px solid ${colours.border}`,
                boxShadow: isCurrent ? '0 0 0 3px #C44A1E55' : 'none',
                outline: isCurrent ? '2px solid #C44A1E' : 'none',
                '&:hover': { filter: 'brightness(0.95)' },
              }}>
              {index + 1}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuestionNavigator;
